import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BriefcaseBusiness, DatabaseBackup, Download, Search, Trash2, UserCheck, UserRound, UserX } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Pagination, { paginate } from '../../components/Pagination';
import RowActions from '../Users/RowActions';
import '../Users/Users.css'; // the three-dot menu styles
import API from '../../utils/api';
import { onAdminRefresh } from '../../utils/adminSocket';
import '../IncompleteRegistrations/IncompleteRegistrations.css';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import './DeactivatedAccounts.css';

// Members and service providers who chose "Deactivate my account". Their data is kept (hidden, cannot sign in, and the mobile
// number / email can be registered again). From here the admin can delete one for good.
const DeactivatedAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [role, setRole] = useState('all');
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState('');
    const [confirm, confirmDialog] = useConfirmDialog();
    const [busyId, setBusyId] = useState('');            // account being backed up / made active ('all' = the full backup)
    const [backedUp, setBackedUp] = useState(() => new Set()); // accounts whose backup was downloaded in this visit

    const load = useCallback(async () => {
        try {
            const response = await API.get('/admin/deactivated-accounts');
            setAccounts(response.data.accounts || []);
        } catch (error) {
            if (error.response?.status !== 429) toast.error(error.response?.data?.message || 'Could not load deactivated accounts');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const off = onAdminRefresh(load);
        return () => off();
    }, [load]);

    const nameOf = (account) => (account.role === 'service'
        ? (account.businessName || account.ownerName)
        : (account.profileName || account.name)) || 'Name not entered';

    const counts = useMemo(() => ({
        member: accounts.filter(a => a.role === 'member').length,
        service: accounts.filter(a => a.role === 'service').length,
    }), [accounts]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return accounts.filter(account => {
            if (role !== 'all' && account.role !== role) return false;
            if (!needle) return true;
            return [nameOf(account), account.mobile, account.email, account.memberId, account.providerId, account.profile?.city]
                .filter(Boolean).some(value => String(value).toLowerCase().includes(needle));
        });
    }, [accounts, query, role]);

    // 10 accounts per page; after a search, a tab change, a delete or "Make active" the page may no longer exist: go back to the last one
    const PAGE_SIZE = 10;
    useEffect(() => {
        const lastPage = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
        if (page > lastPage) setPage(lastPage);
    }, [visible.length, page]);

    // ZIP of Excel sheets with every detail of the deactivated account(s), same format as the Backup page. account = one account, or none for all.
    const downloadBackup = async (account) => {
        const key = account ? account._id : 'all';
        setBusyId(key);
        try {
            const scope = account ? (account.role === 'service' ? 'providers' : 'members') : 'both';
            const res = await API.get('/admin/backup/download', {
                params: { period: 'all', scope, deactivated: '1', ...(account ? { id: account._id } : {}) },
                responseType: 'blob', timeout: 300000,
            });
            const disposition = res.headers?.['content-disposition'] || '';
            const name = (/filename="?([^";]+)"?/i.exec(disposition) || [])[1] || 'deactivated-accounts-backup.zip';
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url; link.download = name;
            document.body.appendChild(link); link.click(); link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            setBackedUp(current => {
                const next = new Set(current);
                if (account) next.add(account._id); else accounts.forEach(a => next.add(a._id));
                return next;
            });
            toast.success('Backup downloaded');
        } catch (error) {
            let message = 'Could not create the backup';
            try { message = JSON.parse(await error.response.data.text()).message || message; } catch { /* keep the default */ }
            toast.error(message);
        } finally {
            setBusyId('');
        }
    };

    const makeActive = async (account) => {
        if (!(await confirm({
            title: `Make ${nameOf(account)} active again?`,
            message: `${account.mobile ? `+91 ${account.mobile}` : 'This account'} is switched back on.`,
            items: ['The profile or listings become visible again', 'The person can sign in with the same mobile number and password'],
            confirmLabel: 'Make active', icon: UserCheck,
        }))) return;
        setBusyId(account._id);
        try {
            const response = await API.post(`/admin/deactivated-accounts/${account._id}/restore`);
            setAccounts(current => current.filter(item => item._id !== account._id));
            toast.success(response.data.message || 'Account is active again');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not make the account active');
        } finally {
            setBusyId('');
        }
    };

    const remove = async (account) => {
        const label = `${nameOf(account)} (${account.mobile || 'no mobile'})`;
        const reminder = backedUp.has(account._id) ? '' : 'You have not downloaded a backup of this account yet. Cancel and click "Backup" first if you may need the data later. ';
        if (!(await confirm({
            title: `Delete ${label} permanently?`,
            message: `${reminder}This cannot be undone.`,
            items: ['The account, profile and login', 'Photos, documents and chats', 'Everything else attached to it'],
            confirmLabel: 'Delete permanently', danger: true,
        }))) return;
        setDeletingId(account._id);
        try {
            await API.delete(`/admin/deactivated-accounts/${account._id}`);
            setAccounts(current => current.filter(item => item._id !== account._id));
            toast.success('Account deleted permanently');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not delete the account');
        } finally {
            setDeletingId('');
        }
    };

    const total = counts.member + counts.service;

    return (
        <div className="incomplete-layout">
            <Sidebar />
            <main className="admin-content incomplete-content">
                <Navbar title={<span className="incomplete-title"><UserX size={19} /> Deactivated Accounts</span>} />
                <div className="incomplete-page">
                    <header className="incomplete-heading">
                        <div>
                            <h2>Deactivated accounts</h2>
                            <p>People who chose to deactivate their account. Their data is kept here, hidden from the site, and they can register again with the same details. Delete one to remove it for good.</p>
                        </div>
                        <span className="incomplete-total"><UserX size={16} /> {total} deactivated</span>
                    </header>

                    <section className="deactivated-backup">
                        <div>
                            <strong><DatabaseBackup size={16} /> Back up before deleting</strong>
                            <p>Download every detail of the deactivated accounts as a ZIP of Excel sheets (same as the Backup page). Passwords, Aadhaar numbers and ID card images are never included.</p>
                        </div>
                        <button type="button" onClick={() => downloadBackup(null)} disabled={busyId === 'all' || total === 0}>
                            <Download size={16} /> {busyId === 'all' ? 'Preparing the ZIP…' : 'Download backup (all)'}
                        </button>
                    </section>

                    <section className="incomplete-panel">
                        <div className="incomplete-toolbar">
                            <label className="incomplete-search"><Search size={17} /><input type="search" value={query} onChange={event => { setQuery(event.target.value); setPage(1); }} placeholder="Search name, mobile, email, ID or city..." /></label>
                            <div className="incomplete-tabs">
                                {[['all', 'All', total], ['member', 'Members', counts.member], ['service', 'Service providers', counts.service]].map(([value, label, count]) => (
                                    <button type="button" key={value} className={role === value ? 'active' : ''} onClick={() => { setRole(value); setPage(1); }}>{label}<span>{count}</span></button>
                                ))}
                            </div>
                        </div>

                        {loading ? <div className="incomplete-state">Loading deactivated accounts...</div> : visible.length === 0 ? (
                            <div className="incomplete-state"><UserX size={36} /><strong>No deactivated accounts</strong><span>Accounts that members or service providers deactivate will appear here.</span></div>
                        ) : (
                            <>
                                <div className="deactivated-table-wrap">
                                    <table className="deactivated-table">
                                        <thead>
                                            <tr>
                                                <th>#</th><th>Account</th><th>Mobile</th><th>Email</th><th>Location</th><th>Reason</th><th>Plan</th><th>Deactivated</th><th className="is-actions">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginate(visible, page, PAGE_SIZE).map((account, index) => {
                                                const isService = account.role === 'service';
                                                const location = [account.profile?.city, account.profile?.state].filter(Boolean).join(', ');
                                                return (
                                                    <tr key={account._id}>
                                                        <td className="is-num">{String((page - 1) * PAGE_SIZE + index + 1).padStart(2, '0')}</td>
                                                        <td>
                                                            <div className="deactivated-who">
                                                                <span className={`incomplete-role-icon ${isService ? 'service' : 'member'}`}>{isService ? <BriefcaseBusiness size={17} /> : <UserRound size={17} />}</span>
                                                                <div><strong className="deactivated-cut">{nameOf(account)}</strong><small>{isService ? 'Service provider' : 'Member'} · {account.providerId || account.memberId || 'No ID'}</small></div>
                                                            </div>
                                                        </td>
                                                        <td className="deactivated-nowrap">{account.mobile ? `+91 ${account.mobile}` : '—'}</td>
                                                        <td><span className="deactivated-cut">{account.email || '—'}</span></td>
                                                        <td><span className="deactivated-cut">{location || '—'}</span></td>
                                                        <td><span className="deactivated-cut">{account.deactivationReason || '—'}</span></td>
                                                        <td>{account.isPremium ? 'Premium' : 'Free'}</td>
                                                        <td className="deactivated-nowrap" title={account.deactivatedAt ? new Date(account.deactivatedAt).toLocaleString() : ''}>
                                                            {account.deactivatedAt ? new Date(account.deactivatedAt).toLocaleDateString('en-IN') : '—'}
                                                            {backedUp.has(account._id) && <small className="deactivated-backedup">Backed up</small>}
                                                        </td>
                                                        <td className="is-actions">
                                                            <RowActions label={`Actions for ${nameOf(account)}`} items={[
                                                                { label: busyId === account._id ? 'Working…' : 'Download backup', Icon: Download, tone: 'primary', onClick: () => downloadBackup(account) },
                                                                { label: 'Make active', Icon: UserCheck, tone: 'success', onClick: () => makeActive(account) },
                                                                { label: 'Delete permanently', Icon: Trash2, tone: 'danger', onClick: () => remove(account) },
                                                            ]} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination page={page} total={visible.length} pageSize={PAGE_SIZE} onChange={setPage} />
                            </>
                        )}
                    </section>
                </div>
            </main>
            {confirmDialog}
        </div>
    );
};

export default DeactivatedAccounts;
